import { useTranslation } from "react-i18next";
import EmployeeRow from "./EmployeeRow";
import { UserMinusIcon } from "@heroicons/react/24/outline";
import Button from "@/components/common/Button";

interface EmployeeTableProps {
  filteredEmployees: any[];
  departments: any[];
  onTerminateEmployee: (employeeId: string) => void;
  isDepartmentAdmin: boolean;
}

const EmployeeTable = ({
  filteredEmployees,
  departments,
  onTerminateEmployee,
  isDepartmentAdmin,
}: EmployeeTableProps) => {
  const { t } = useTranslation();

  if (filteredEmployees.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 dark:text-gray-400">
          {t("admin.noEmployeesFound", "No employees found")}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
            <tr>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.name", "Name")}
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.email", "Email")}
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.role", "Role")}
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("admin.department", "Department")}
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.status", "Status")}
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.actions", "Actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
            {filteredEmployees.map((employee) => (
              <EmployeeRow
                key={employee._id}
                employee={employee}
                departments={Array.isArray(departments) ? departments : []}
                onTerminateEmployee={onTerminateEmployee}
                isDepartmentAdmin={isDepartmentAdmin}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Tablet Table */}
      <div className="hidden sm:block lg:hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.name", "Name")}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.email", "Email")}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.role", "Role")}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.status", "Status")}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {t("common.actions", "Actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
            {filteredEmployees.map((employee) => (
              <EmployeeRow
                key={employee._id}
                employee={employee}
                departments={Array.isArray(departments) ? departments : []}
                onTerminateEmployee={onTerminateEmployee}
                isDepartmentAdmin={isDepartmentAdmin}
                isTablet={true}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-3 p-4">
        {filteredEmployees.map((employee) => (
          <div key={employee._id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start min-w-0 flex-1">
                <div className="flex-shrink-0 h-10 w-10 mr-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 flex items-center justify-center ring-2 ring-gray-200 dark:ring-gray-700 shadow-sm">
                    <span className="text-sm font-bold text-primary-700 dark:text-primary-300">
                      {employee.fullName?.charAt(0) || "?"}
                    </span>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {employee.fullName || "Unknown"}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {employee.email || "No email"}
                  </p>
                </div>
              </div>
              <div className="ml-2 flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  {employee.isActive ? (
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  ) : (
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  )}
                  <span className={`text-xs font-semibold ${
                    employee.isActive 
                      ? "text-green-700 dark:text-green-300" 
                      : "text-red-700 dark:text-red-300"
                  }`}>
                    {employee.isActive ? t("common.active", "Active") : t("common.inactive", "Inactive")}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-3 text-xs text-gray-500 dark:text-gray-400">
              <div>
                <span className="font-medium">{t("common.role")}:</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 dark:from-blue-900 dark:to-blue-800 dark:text-blue-200 shadow-sm ml-1">
                  {(() => {
                    switch (employee.role) {
                      case "company_admin":
                        return t("admin.companyAdmin", "Company Admin");
                      case "department_admin":
                        return t("admin.departmentAdmin", "Department Admin");
                      case "employee":
                        return t("common.employee", "Employee");
                      default:
                        return employee.role;
                    }
                  })()}
                </span>
              </div>
              <div>
                <span className="font-medium">{t("admin.department")}:</span>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {(() => {
                    if (employee.role === "company_admin") {
                      return (
                        <span className="text-gray-400 italic">
                          {t("admin.noDepartment", "No Department")}
                        </span>
                      );
                    }

                    if (!employee.departmentId) {
                      return (
                        <span className="text-yellow-600 dark:text-yellow-400 italic">
                          {t("admin.unassigned", "Unassigned")}
                        </span>
                      );
                    }

                    const department = Array.isArray(departments) ? departments.find(dept => 
                      (dept._id || dept.id) === employee.departmentId
                    ) : null;

                    return department ? (
                      <span className="text-blue-600 dark:text-blue-400 font-medium">
                        {department.name}
                      </span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400 italic">
                        {t("admin.departmentNotFound", "Department Not Found")}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              leftIcon={<UserMinusIcon className="h-3 w-3" />}
              onClick={() => onTerminateEmployee(employee._id)}
              className="w-full text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
            >
              {t("admin.terminate", "Terminate")}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmployeeTable;
